import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  ContactData,
  ContactDataInterface,
} from '../../../../interfaces/contact-data.interface';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { ApiService } from '../../../services/api.service';
import { RecaptchaService } from '../../../services/recaptcha.service';
import { take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import * as moment from 'moment';
import { environment } from '../../../../environments/environment';
import {
  captureFormSubmitException,
  formFieldsSentryExtra,
} from '../../../utils/sentry-http-error.util';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
})
export class ContactComponent implements OnInit, OnDestroy {
  contactDto: ContactDataInterface;
  contactFormGroup: UntypedFormGroup;
  showAll = false;
  recaptchaPending = false;
  submitThanks = false;
  thankYouCooldown = false;
  private thankYouTimer: ReturnType<typeof setTimeout> | null = null;
  readonly formEnabled: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private apiService: ApiService,
    private recaptchaService: RecaptchaService,
    private confirmationService: ConfirmationService,
    private transloco: TranslocoService
  ) {
    this.formEnabled = this.recaptchaService.isConfigured();
  }

  get submitInactive(): boolean {
    return (
      !this.formEnabled ||
      this.recaptchaPending ||
      this.thankYouCooldown ||
      this.contactFormGroup?.invalid
    );
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    if (this.thankYouTimer) {
      clearTimeout(this.thankYouTimer);
    }
  }

  warmRecaptcha(): void {
    this.recaptchaService.warmup();
  }

  initializeForm(): void {
    this.contactDto = new ContactData();
    this.contactFormGroup = this.fb.group({
      name: [this.contactDto.name, [Validators.required]],
      brand: [this.contactDto.brand, [Validators.required]],
      yearOfProduction: [
        this.contactDto.yearOfProduction,
        [
          Validators.required,
          RxwebValidators.digit(),
          Validators.min(1950),
          Validators.max(Number(moment().format('YYYY'))),
        ],
      ],
      phoneNo: [
        this.contactDto.phoneNo,
        [Validators.required, RxwebValidators.digit()],
      ],
      model: [this.contactDto.model, [Validators.required]],
      value: [
        this.contactDto.value,
        [Validators.required, RxwebValidators.digit()],
      ],
      email: [
        this.contactDto.email,
        [Validators.required, RxwebValidators.email()],
      ],
      mileage: [
        this.contactDto.mileage,
        [
          Validators.required,
          RxwebValidators.digit(),
          Validators.min(0),
        ],
      ],
      gapPeriod: [
        this.contactDto.gapPeriod,
        [
          Validators.required,
          RxwebValidators.digit(),
          Validators.min(12),
          Validators.max(60),
        ],
      ],
      textMsg: [this.contactDto.textMsg, [Validators.required]],
      agreement1: [this.contactDto.agreement1, [Validators.requiredTrue]],
      agreement2: [this.contactDto.agreement2, [Validators.requiredTrue]],
    });
  }

  async onSubmit(): Promise<void> {
    if (this.submitInactive) {
      return;
    }

    this.recaptchaPending = true;
    this.submitThanks = false;
    try {
      const recaptchaToken = await this.recaptchaService.execute('contact_form');
      this.contactDto = new ContactData({
        ...this.contactFormGroup.value,
        recaptchaToken,
      });
      await this.apiService
        .sendContactEmail(this.contactDto)
        .pipe(take(1))
        .toPromise();
      this.initializeForm();
      this.showThanks();
    } catch (error: unknown) {
      this.reportSubmitFailure(
        error,
        error instanceof HttpErrorResponse ? 'api' : 'recaptcha',
      );
      if (error instanceof HttpErrorResponse) {
        this.handleSubmitError(error);
      } else {
        console.warn('[GAP contact form] reCAPTCHA before submit failed', error);
        this.confirmationService.confirm({
          message: this.transloco.translate('contact.confirmation.error'),
          rejectVisible: false,
          acceptLabel: this.transloco.translate('contact.confirmation.accept'),
        });
      }
    } finally {
      this.recaptchaPending = false;
    }
  }

  private reportSubmitFailure(
    error: unknown,
    phase: 'recaptcha' | 'api',
  ): void {
    const httpError = error instanceof HttpErrorResponse ? error : null;
    captureFormSubmitException(error, {
      form: 'gap_contact',
      action: 'contact_form',
      phase,
      site: 'gaponline.pl',
      status: httpError ? httpError.status : 0,
      apiBase: environment.apiBase,
      production: environment.production,
      formEnabled: this.formEnabled,
      hostname:
        typeof window !== 'undefined' ? window.location.hostname : null,
      href: typeof window !== 'undefined' ? window.location.href : null,
      recaptchaTokenPresent: Boolean(this.contactDto?.recaptchaToken),
      recaptchaTokenLength: this.contactDto?.recaptchaToken?.length ?? 0,
      ...formFieldsSentryExtra(this.contactFormGroup),
    });
  }

  private handleSubmitError(error: HttpErrorResponse): void {
    if (!environment.production) {
      console.warn('[GAP contact form] API error', {
        status: error.status,
        body: error.error,
      });
    }

    if (error.status === 403) {
      const agreement1 = this.contactFormGroup.get('agreement1');
      if (!agreement1.value) {
        agreement1.markAsDirty();
        agreement1.markAllAsTouched();
      }

      const agreement2 = this.contactFormGroup.get('agreement2');
      if (!agreement2.value) {
        agreement1.markAsDirty();
        agreement1.markAllAsTouched();
      }
      return;
    }

    this.confirmationService.confirm({
      message: this.transloco.translate('contact.confirmation.error'),
      rejectVisible: false,
      acceptLabel: this.transloco.translate('contact.confirmation.accept'),
    });
  }

  private showThanks(): void {
    this.submitThanks = true;
    this.thankYouCooldown = true;
    if (this.thankYouTimer) {
      clearTimeout(this.thankYouTimer);
    }
    this.thankYouTimer = setTimeout(() => {
      this.thankYouCooldown = false;
      this.thankYouTimer = null;
    }, 2000);
  }

  toggleShowAll(): void {
    this.showAll = !this.showAll;
  }
}
